import { useEffect, useState } from "react";
import { Clock, Mail, Loader2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const SALES_EMAIL = "sentinel@defdossier.com";

function daysUntil(iso) {
  if (!iso) return null;
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return null;
  const ms = end.getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

/**
 * Discreet strip that surfaces when the user's org trial is within 7 days
 * of expiring (or has expired). Two CTAs:
 *   - Talk to sales (mailto, opens user's mail client)
 *   - Request 14-day extension (POSTs to /me/org/request-extension which
 *     drops a "trial_extension_request" lead into the platform admin inbox)
 *
 * Renders nothing for: non-authed users, non-trial orgs, or trials with
 * more than 7 days remaining.
 */
export default function TrialBanner() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null); // { ok: bool, msg: string }
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    api.get("/me/org")
      .then((r) => setOrg(r.data))
      .catch(() => {});
  }, [user?.id]);

  if (!user?.id || !org || dismissed) return null;
  if (org.subscription_status !== "trial") return null;

  const daysLeft = daysUntil(org.trial_ends_at);
  if (daysLeft === null) return null;
  if (daysLeft > 7) return null;

  const expired = daysLeft < 0;
  const accent = expired ? "var(--red)" : "var(--amber)";
  const label = expired
    ? `Trial expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? "" : "s"} ago`
    : daysLeft === 0
      ? "Trial expires today"
      : `Trial ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`;

  const subject = encodeURIComponent(`[${org.name}] DefDossier — trial / pricing`);
  const body = encodeURIComponent(
    `Hi DefDossier team,\n\n` +
    `Our trial for ${org.name} ${expired ? "has just ended" : `ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}. ` +
    `I'd like to discuss pricing and next steps.\n\n` +
    `Seats currently used: ${org.seats_used || 0} / ${org.seat_limit || "?"}.\n\n` +
    `— ${user.name || user.email}`
  );

  const requestExtension = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post("/me/org/request-extension");
      setOutcome({ ok: true, msg: res.data?.message || "Request received." });
    } catch (e) {
      setOutcome({
        ok: false,
        msg: e?.response?.data?.detail || "Could not submit — try emailing sales directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="border-b"
      style={{ borderColor: accent, backgroundColor: "rgba(0,0,0,0.4)" }}
      data-testid="trial-banner"
    >
      <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          {expired
            ? <AlertTriangle size={14} style={{ color: accent }} strokeWidth={1.5} />
            : <Clock size={14} style={{ color: accent }} strokeWidth={1.5} />}
          <span
            className="font-mono text-[11px] tracking-widest uppercase"
            style={{ color: accent }}
            data-testid="trial-banner-label"
          >
            {label}
          </span>
          {outcome && (
            <span
              className="font-mono text-[10px] tracking-widest uppercase"
              style={{ color: outcome.ok ? "var(--green)" : "var(--red)" }}
              data-testid="trial-banner-outcome"
            >
              · {outcome.msg}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {!outcome?.ok && (
            <button
              onClick={requestExtension}
              disabled={submitting}
              className="font-mono text-[10px] tracking-widest uppercase border px-3 py-1.5 hover:bg-[var(--surface)] disabled:opacity-50"
              style={{ borderColor: accent, color: accent }}
              data-testid="trial-banner-extend"
            >
              {submitting ? <Loader2 size={11} className="animate-spin inline" /> : "Request 14-day extension"}
            </button>
          )}
          <a
            href={`mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`}
            className="font-mono text-[10px] tracking-widest uppercase border border-[var(--cyan)] text-[var(--cyan)] px-3 py-1.5 hover:bg-[var(--surface)] inline-flex items-center gap-1.5"
            data-testid="trial-banner-sales"
          >
            <Mail size={11} /> Talk to sales
          </a>
          <button
            onClick={() => setDismissed(true)}
            className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] hover:text-white px-2"
            aria-label="Dismiss"
            data-testid="trial-banner-dismiss"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
